import { Building } from '@model/building';
import { Promotion } from '@model/promotion';
import { Globals } from '@theme/utils/globals';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ValidatorsEnums } from '@theme/enums/validators.enums';
import { EmitterService } from '@service/emitter/emitter.service';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BuildingService } from '@service/building/building.service';
import { PromotionService } from '@service/promotion/promotion.service';
import { HomeTypeService } from '@service/home-type/home-type.service';
import { HomeType } from '@model/home-type';
import { ToastrService } from 'ngx-toastr';

// Interface pour un type de maison dans un niveau
interface TypeMaisonNiveau {
  typeUuid: string;
  nombre: number;
  superficie: number;
  montant: number;
  baseActualisation: number;
}

// Interface pour un niveau avec ses types de maisons
interface NiveauConfig {
  numero: number;
  typesMaisons: TypeMaisonNiveau[];
  expanded: boolean;
  // Propriétés temporaires pour l'ajout
  newType: string;
  newNombre: number;
  newSuperficie: number;
  newMontant: number;
  newBaseActualisation: number;
}

// Interface pour un appartement généré
interface AppartementGenerated {
  uuid?: string;
  porte: string;
  etage: number;
  etageLabel: string;
  type: string;
  superficie: number;
  montant: number;
  baseActualisation: number;
}

@Component({
  selector: 'app-building-add',
  templateUrl: './building-add.component.html',
  styleUrls: ['./building-add.component.scss']
})
export class BuildingAddComponent implements OnInit {
  title: string = '';
  edit: boolean = false;
  form: FormGroup;
  pieceForm: FormGroup;
  equipementForm: FormGroup;
  galleryForm: FormGroup;
  videoForm: FormGroup;
  planForm: FormGroup;
  submit: boolean = false;
  building: Building;
  buildings: Building[] = [];
  promotions: Promotion[] = [];
  sousPromotions: Promotion[] = [];
  homeTypes: HomeType[] = [];
  promotion: Promotion;
  promotionSelected: any;
  sousPromotionSelected: any;
  required = Globals.required;

  // Propriétés pour la configuration niveau par niveau
  niveauxConfig: NiveauConfig[] = [];
  appartementsGenerated: AppartementGenerated[] = [];

  // Propriétés pour la configuration du formatage des numéros
  radicalCode: string = ''; // Non utilisé pour les bâtiments, gardé pour cohérence UI
  startNumber: number = 1;
  numberingMode: string = 'continue';

  typeRow = [
    { label: 'Bâtiment inexistant', value: 'ADD' },
    { label: 'Bâtiment existant', value: 'UPDATE' }
  ];

  constructor(
    public modal: NgbActiveModal,
    private formBuild: FormBuilder,
    private emitter: EmitterService,
    private buildingService: BuildingService,
    private homeTypeService: HomeTypeService,
    private _changeDetector: ChangeDetectorRef,
    private promotionService: PromotionService,
    private toastr: ToastrService
  ) {
    this.edit = this.buildingService.edit;
    this.building = this.buildingService.getBuilding();
    this.title = (!this.edit) ? 'Ajouter un bâtiment' : 'Modifier le bâtiment ' + this.building?.libelle + ' de la promotion ' + this.building?.promotion?.libelle;
    this.newForm();
    if (!this.edit) {
      this.promotionService.getList().subscribe((res) => {
        this.promotions = res;
        this.promotions = this.promotions.filter(promo => promo.type != 'TYPE_A');
      })
      this.homeTypeService.getList().subscribe((res) => {
        this.homeTypes = res;
      })
    } else {
      this.promotions.push(this.building.promotion)
    }
  }

  ngOnInit() {
    this.editForm();
  }

  newForm() {
    this.form = this.formBuild.group({
      uuid: [null],
      id: [null],
      type: ['ADD', [Validators.required]],
      promotion: [null, [Validators.required]],
      sousPromotion: [null],
      niveau: [null, [Validators.required, Validators.pattern(ValidatorsEnums.number), Validators.min(1)]],
      libelle: [null, [Validators.required]],
      superficie: [0, [Validators.required, Validators.pattern(ValidatorsEnums.number), Validators.min(0)]],
      appartements: this.formBuild.array([]),
      piece: [null],
      equipement: [null],
      video: [null],
      gallery: [null],
      plan: [null]
    });

    this.pieceForm = this.formBuild.group({
      chambre: [0],
      douche: [0],
      salon: [0],
      cuisine: [0],
      garage: [0],
    });

    this.equipementForm = this.formBuild.group({
      wifi: [false],
      clim: [false],
      securite: [false],
      parking: [false],
      incendie: [false],
      urgence: [false],
      piscine: [false],
    });

    this.galleryForm = this.formBuild.group({
      files: [null],
    });

    this.planForm = this.formBuild.group({
      files: [null],
    });

    this.videoForm = this.formBuild.group({
      link: [null],
    });

    // Gestion des changements de type
    this.form.get('type').valueChanges.subscribe(res => {
      this.form.get('uuid').setValue(null);
      this.form.get('niveau').setValue(null);
      this.form.get('libelle').setValue(null);
      this.form.get('superficie').setValue(null);
      this.resetNiveauxConfig();

      if (!this.edit && res === 'ADD') {
        this.form.get('uuid').clearValidators();
        this.form.get('niveau').enable();
        this.form.get('libelle').enable();
        this.form.get('superficie').enable();
      } else if (!this.edit && res === 'UPDATE') {
        this.loadBuilding(this.f.sousPromotion.value ?? this.f.promotion.value);
        this.form.get('uuid').setValidators(Validators.required);
        this.form.get('niveau').disable();
        this.form.get('libelle').disable();
        this.form.get('superficie').disable();
      }
      this.form.get('uuid').updateValueAndValidity();
    });

    // Gestion de la sélection d'un bâtiment existant
    this.form.get('uuid').valueChanges.subscribe(res => {
      if (!this.edit && res) {
        this.building = this.buildings.find(build => build.uuid === res);
        this.form.get('libelle').setValue(this.building?.libelle);
        this.form.get('niveau').setValue(this.building?.niveau);
        this.form.get('superficie').setValue(this.building?.superficie);
        
        // Charger les appartements existants pour déterminer le dernier numéro
        this.loadExistingAppartementsCount();
      }
    });

    // Surveiller les changements du libellé pour régénérer si nécessaire
    this.form.get('libelle').valueChanges.subscribe(newLibelle => {
      if (!this.edit && newLibelle && this.appartementsGenerated.length > 0) {
        // Proposer de régénérer les numéros avec le nouveau libellé
        this.onLibelleChanged(newLibelle);
      }
    });
  }

  editForm() {
    if (this.edit) {
      const data = { ...this.buildingService.getBuilding() };
      this.form.get('uuid').setValue(data.uuid);
      this.form.get('type').setValue('UPDATE');
      this.promotionSelected = data.promotion.uuid;
      this.setPromotionUuid(data.promotion);
      this.form.patchValue(data);
      this.f.promotion.setValue(data?.promotion?.uuid);
    }
  }

  // === MÉTHODES POUR LA CONFIGURATION NIVEAU PAR NIVEAU ===

  onChangeNiveaux() {
    const nombreNiveaux = parseInt(this.f.niveau.value) || 0;
    this.resetNiveauxConfig();
    
    if (nombreNiveaux > 0) {
      for (let i = 1; i <= nombreNiveaux; i++) {
        this.niveauxConfig.push({
          numero: i,
          typesMaisons: [],
          expanded: i === 1, // Premier niveau ouvert par défaut
          newType: '',
          newNombre: 1,
          newSuperficie: 0,
          newMontant: 0,
          newBaseActualisation: 0
        });
      }
    }
  }

  resetNiveauxConfig() {
    this.niveauxConfig = [];
    this.appartementsGenerated = [];
    this.appartement.clear();
    // Reset des paramètres de formatage
    this.radicalCode = '';
    this.startNumber = 1;
    this.numberingMode = 'continue';
  }

  toggleNiveauExpansion(niveauIndex: number) {
    if (this.niveauxConfig[niveauIndex]) {
      this.niveauxConfig[niveauIndex].expanded = !this.niveauxConfig[niveauIndex].expanded;
    }
  }

  addTypeMaisonToNiveau(niveauIndex: number) {
    const niveau = this.niveauxConfig[niveauIndex];
    if (!niveau) return;

    // Validation des champs
    if (!niveau.newType || niveau.newNombre <= 0 || niveau.newSuperficie <= 0 || 
        niveau.newMontant <= 0 || niveau.newBaseActualisation <= 0) {
      this.toast('Veuillez remplir tous les champs requis', 'Erreur', 'error');
      return;
    }

    // Vérifier si le type n'existe pas déjà dans ce niveau
    const existingType = niveau.typesMaisons.find(t => t.typeUuid === niveau.newType);
    if (existingType) {
      this.toast('Ce type existe déjà dans ce niveau', 'Erreur', 'error');
      return;
    }

    // Ajouter le type de maison
    const newTypeMaison: TypeMaisonNiveau = {
      typeUuid: niveau.newType,
      nombre: niveau.newNombre,
      superficie: niveau.newSuperficie,
      montant: niveau.newMontant,
      baseActualisation: niveau.newBaseActualisation
    };

    niveau.typesMaisons.push(newTypeMaison);

    // Reset des champs temporaires
    niveau.newType = '';
    niveau.newNombre = 1;
    niveau.newSuperficie = 0;
    niveau.newMontant = 0;
    niveau.newBaseActualisation = 0;

    // Reset des appartements générés car la configuration a changé
    this.appartementsGenerated = [];
    this.appartement.clear();

    this.toast('Type d\'appartement ajouté avec succès', 'Succès', 'success');
  }

  removeTypeMaisonFromNiveau(niveauIndex: number, typeMaisonIndex: number) {
    Swal.fire({
      title: 'Confirmation',
      text: 'Êtes-vous sûr de vouloir supprimer ce type d\'appartement ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Supprimer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#dc3545',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        this.niveauxConfig[niveauIndex].typesMaisons.splice(typeMaisonIndex, 1);
        
        // Reset des appartements générés
        this.appartementsGenerated = [];
        this.appartement.clear();
        
        this.toast('Type d\'appartement supprimé', 'Succès', 'success');
      }
    });
  }

  // === MÉTHODES POUR LE FORMATAGE DES NUMÉROS - SPÉCIFIQUE BÂTIMENT ===

  // Charger le nombre d'appartements existants dans le bâtiment
  loadExistingAppartementsCount() {
    // Cette méthode peut être étendue pour récupérer le nombre d'appartements
    // déjà existants dans le bâtiment sélectionné
  }

  // Calculer le prochain numéro de porte disponible
  getNextAvailablePorteNumber(): number {
    if (this.numberingMode === 'restart') {
      return this.startNumber;
    }
    
    if (this.appartementsGenerated.length === 0) {
      // Si on est en mode UPDATE et qu'un bâtiment est sélectionné,
      // partir du nombre d'appartements existants
      if (this.f.type.value === 'UPDATE' && this.building) {
        const existingCount = this.building.nbrMaison || 0;
        return existingCount + this.startNumber;
      }
      return this.startNumber;
    }
    
    // Mode continue : analyser les numéros existants
    const existingPortes = this.appartementsGenerated.map(app => {
      const porteValue = app.porte;
      // Extraire le numéro après "-A"
      // Ex: "Bâtiment A-A5" -> 5
      const match = porteValue.match(/-A(\d+)$/);
      return match ? parseInt(match[1]) : this.startNumber;
    });
    
    const maxNumber = Math.max(...existingPortes, this.startNumber - 1);
    return maxNumber + 1;
  }

  // Valider le radical (gardé pour cohérence UI mais non utilisé)
  validateRadicalCode(radical: string): boolean {
    const pattern = /^[A-Za-z0-9\s]*$/;
    const isValid = radical === '' || pattern.test(radical.trim());
    
    if (!isValid && radical.trim() !== '') {
      this.toast('Le radical ne doit contenir que des lettres et des chiffres', 'Erreur', 'error');
    }
    
    return isValid;
  }

  // Prévisualiser le format : LibelléBâtiment-A1, LibelléBâtiment-A2, etc.
  getPreviewPorteFormat(): string {
    const libelle = this.f.libelle.value || 'Bâtiment';
    const nextNumber = this.getNextAvailablePorteNumber();
    return `${libelle}-A${nextNumber}, ${libelle}-A${nextNumber + 1}, ${libelle}-A${nextNumber + 2}, ...`;
  }

  // Méthode appelée quand le libellé change
  onLibelleChanged(newLibelle: string) {
    // Ne rien faire automatiquement, l'utilisateur peut utiliser le bouton "Appliquer"
  }

  // Régénérer tous les numéros de porte avec le format : LibelléBâtiment-AX
  regenerateAllPorteNumbers() {
    if (this.appartementsGenerated.length === 0) {
      this.toast('Aucun appartement à régénérer', 'Information', 'info');
      return;
    }

    const libelle = this.f.libelle.value;
    if (!libelle || libelle.trim() === '') {
      this.toast('Veuillez définir un libellé pour le bâtiment', 'Erreur', 'error');
      return;
    }

    Swal.fire({
      title: 'Régénérer les numéros ?',
      text: 'Cette action va recalculer tous les numéros de porte avec les nouveaux paramètres. Continuer ?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Oui, régénérer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#1bc943',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        let currentPorteNumber = this.numberingMode === 'restart' ? 
          this.startNumber : this.getNextAvailablePorteNumber();
        
        // Si mode restart, forcer startNumber
        if (this.numberingMode === 'restart') {
          currentPorteNumber = this.startNumber;
        }
        
        // Régénérer tous les numéros avec le format : LibelléBâtiment-AX
        for (let i = 0; i < this.appartementsGenerated.length; i++) {
          const appartement = this.appartementsGenerated[i];
          
          // Format : LibelléBâtiment-A + numéro
          appartement.porte = `${libelle}-A${currentPorteNumber}`;
          
          // Mettre à jour le FormArray
          const apartmentControl = this.appartement.at(i) as FormGroup;
          if (apartmentControl) {
            apartmentControl.patchValue({
              porte: appartement.porte
            });
          }
          
          currentPorteNumber++;
        }
        
        this.toast('Numéros de porte régénérés avec succès', 'Succès', 'success');
      }
    });
  }

  // Générer tous les appartements avec la codification : LibelléBâtiment-AX
  generateAllAppartements() {
    if (!this.canGenerateAppartements()) {
      this.toast('Veuillez configurer au moins un type d\'appartement', 'Erreur', 'error');
      return;
    }

    const libelle = this.f.libelle.value;
    if (!libelle || libelle.trim() === '') {
      this.toast('Veuillez définir un libellé pour le bâtiment avant de générer les appartements', 'Erreur', 'error');
      return;
    }

    this.appartementsGenerated = [];
    this.appartement.clear();

    let currentPorteNumber = this.getNextAvailablePorteNumber();

    // Générer les appartements niveau par niveau
    for (const niveau of this.niveauxConfig) {
      for (const typeMaison of niveau.typesMaisons) {
        for (let i = 0; i < typeMaison.nombre; i++) {
          // Format : LibelléBâtiment-A + numéro séquentiel
          const porteFormatted = `${libelle}-A${currentPorteNumber}`;

          const appartement: AppartementGenerated = {
            porte: porteFormatted,
            etage: niveau.numero,
            etageLabel: this.getNiveauLibelle(niveau.numero),
            type: typeMaison.typeUuid,
            superficie: typeMaison.superficie,
            montant: typeMaison.montant,
            baseActualisation: typeMaison.baseActualisation
          };

          this.appartementsGenerated.push(appartement);

          // Ajouter au FormArray
          this.appartement.push(
            this.formBuild.group({
              uuid: [null],
              id: [null],
              type: [typeMaison.typeUuid, [Validators.required]],
              etage: [niveau.numero, [Validators.required]],
              porte: [porteFormatted, [Validators.required]],
              montant: [typeMaison.montant, [Validators.required]],
              superficie: [typeMaison.superficie, [Validators.required]],
              baseActualisation: [typeMaison.baseActualisation, [Validators.required]]
            })
          );

          currentPorteNumber++;
        }
      }
    }

    this.toast(`${this.appartementsGenerated.length} appartements générés avec succès`, 'Succès', 'success');
  }

  // === MÉTHODES POUR L'ÉDITION EN LIGNE ===

  updateAppartementPorte(appartement: AppartementGenerated, event: any) {
    const newValue = event.target.value;
    
    // Validation du format : doit contenir "-A" suivi de chiffres
    const formatPattern = /-A\d+$/;
    if (!formatPattern.test(newValue)) {
      this.toast('Le format doit être : LibelléBâtiment-AX (ex: Bâtiment A-A1)', 'Format invalide', 'warning');
    }
    
    // Vérifier l'unicité
    const duplicateAppartement = this.appartementsGenerated.find(app => 
      app !== appartement && app.porte === newValue
    );
    
    if (duplicateAppartement) {
      this.toast('Ce numéro de porte existe déjà', 'Erreur', 'error');
      event.target.value = appartement.porte;
      return;
    }
    
    appartement.porte = newValue;
    this.updateFormArrayFromAppartements();
  }

  updateAppartementSuperficie(appartement: AppartementGenerated, event: any) {
    const newValue = parseFloat(event.target.value) || 0;
    appartement.superficie = newValue;
    this.updateFormArrayFromAppartements();
  }

  updateAppartementMontant(appartement: AppartementGenerated, event: any) {
    const newValue = parseFloat(event.target.value) || 0;
    appartement.montant = newValue;
    this.updateFormArrayFromAppartements();
  }

  updateAppartementBaseActualisation(appartement: AppartementGenerated, event: any) {
    const newValue = parseFloat(event.target.value) || 0;
    appartement.baseActualisation = newValue;
    this.updateFormArrayFromAppartements();
  }

  // Synchroniser le FormArray avec les appartements
  updateFormArrayFromAppartements() {
    // Vider le FormArray
    while (this.appartement.length !== 0) {
      this.appartement.removeAt(0);
    }
    
    // Reconstruire avec les nouvelles valeurs
    this.appartementsGenerated.forEach(appartement => {
      this.appartement.push(
        this.formBuild.group({
          uuid: [appartement.uuid || null],
          id: [null],
          type: [appartement.type, [Validators.required]],
          etage: [appartement.etage, [Validators.required]],
          porte: [appartement.porte, [Validators.required]],
          montant: [appartement.montant, [Validators.required]],
          superficie: [appartement.superficie, [Validators.required]],
          baseActualisation: [appartement.baseActualisation, [Validators.required]]
        })
      );
    });
  }

  // === MÉTHODES UTILITAIRES ===

  getNiveauLibelle(numero: number): string {
    if (numero === 1) {
      return 'Rez-de-chaussée';
    }
    return `${numero}e étage`;
  }

  getTypeMaisonLabel(typeUuid: string): string {
    const homeType = this.homeTypes.find(ht => ht.uuid === typeUuid);
    return homeType ? homeType.libelle : 'Type inconnu';
  }

  getTotalMaisonsNiveau(niveau: NiveauConfig): number {
    return niveau.typesMaisons.reduce((total, type) => total + type.nombre, 0);
  }

  getTotalAppartements(): number {
    return this.niveauxConfig.reduce((total, niveau) => {
      return total + this.getTotalMaisonsNiveau(niveau);
    }, 0);
  }

  getAppartementsByNiveau(numeroNiveau: number): AppartementGenerated[] {
    return this.appartementsGenerated.filter(app => app.etage === numeroNiveau);
  }

  canGenerateAppartements(): boolean {
    return this.niveauxConfig.some(niveau => niveau.typesMaisons.length > 0);
  }

  canProceedToNextStep(): boolean {
    return this.f.type.value === 'UPDATE' || 
           (this.f.type.value === 'ADD' && this.appartementsGenerated.length > 0);
  }

  // === MÉTHODES EXISTANTES ADAPTÉES ===

  setPromotionUuid(promotion) {
    this.promotion = promotion;
    if (promotion) {
      this.f.promotion.setValue(promotion.uuid);
      this.onChangePromotion(promotion);
      this.loadBuilding(promotion.uuid);
      this.loadSousPromotions();
    }
  }

  loadBuilding(uuid) {
    this.buildings = [];
    if (uuid && this.f.type.value === 'UPDATE') {
      this.buildingService.getList(uuid).subscribe((res) => {
        this.buildings = res;
        this._changeDetector.markForCheck();
      })
    }
  }

  loadSousPromotions() {
    this.sousPromotions = [];
    this.promotionService.getList(this.f.promotion.value, 'SOUS_LISTE').subscribe((res) => {
      this.sousPromotions = res;
      this.sousPromotions = this.sousPromotions.filter(promo => promo.type != 'TYPE_A');
    })
  }

  uploadFile(data, type) {
    if (type === "GALLERY") {
      this.galleryForm.get('files').setValue(data);
    }
    if (type === "PLAN") {
      this.planForm.get('files').setValue(data);
    }
  }

  onChangePromotion(event) {
    if (event) {
      this.promotionService.getSingle(event?.uuid).subscribe((res) => {
        this.promotion = res;
        if (!this.edit) {
          this.loadBuilding(res.uuid);
          this.loadSousPromotions();
        }
        if (res?.type === 'TYPE_A') {
          Swal.fire({
            title: 'ATTENTION',
            text: 'Il est impossible d\'ajouter des bâtiments/immeuble sur des promotions de type A.',
            icon: 'error',
            showCancelButton: false,
            showCloseButton: false
          }).then((willDelete) => {
            this.promotion = null;
            this.f.promotion.setValue(null)
            this.f.sousPromotion.setValue(null)
          });
        }
      })
    } else {
      this.promotion = null;
      this.f.promotion.setValue(null)
      this.f.sousPromotion.setValue(null)
    }
  }

  onChangeSousPromotion(event) {
    if (event) {
      this.promotionService.getSingle(event?.uuid).subscribe((res) => {
        this.promotion = res;
        this.f.sousPromotion.setValue(res.uuid)
        if (!this.edit && res?.type) {
          this.loadBuilding(res.uuid);
        }
        if (res?.type === 'TYPE_A') {
          Swal.fire({
            title: 'ATTENTION',
            text: 'Il est impossible d\'ajouter des bâtiments/immeuble sur des promotions de type A.',
            icon: 'error',
            showCancelButton: false,
            showCloseButton: false
          }).then((willDelete) => {
            this.f.sousPromotion.setValue(null)
          });
        }
      })
    } else {
      this.f.sousPromotion.setValue(null)
    }
  }

  onSubmit() {
    this.submit = true;
    if (this.f.sousPromotion.value != null) {
      this.f.promotion.setValue(this.f.sousPromotion.value)
    }
    
    // Préparer les données
    this.f.plan.setValue(this.planForm.getRawValue());
    this.f.piece.setValue(this.pieceForm.getRawValue());
    this.f.video.setValue(this.videoForm.getRawValue());
    this.f.gallery.setValue(this.galleryForm.getRawValue());
    this.f.equipement.setValue(this.equipementForm.getRawValue());
    
    if (this.form.valid) {
      // Vérifier qu'il y a des appartements si c'est un nouveau bâtiment
      if (this.f.type.value === 'ADD' && this.appartementsGenerated.length === 0) {
        this.toast('Veuillez générer les appartements avant d\'enregistrer', 'Erreur', 'error');
        return;
      }

      // Synchroniser le FormArray
      if (this.appartementsGenerated.length > 0) {
        this.updateFormArrayFromAppartements();
      }

      console.log('Données envoyées:', this.form.getRawValue());

      this.buildingService.add(this.form.getRawValue()).subscribe(res => {
        if (res?.status === 'success') {
          this.modal.close('ferme');
          if (this.form.getRawValue().uuid) {
            this.emitter.emit({ action: 'BUILDING_UPDATED', payload: res?.data });
          } else {
            this.emitter.emit({ action: 'BUILDING_ADD', payload: res?.data });
          }
        }
      }, error => {
        console.error('Erreur lors de la soumission:', error);
        this.toast('Une erreur est survenue lors de l\'enregistrement', 'Erreur', 'error');
      });
    } else {
      this.toast('Veuillez corriger les erreurs du formulaire', 'Erreur', 'error');
      console.log('Erreurs du formulaire:', this.form.errors);
      return;
    }
  }

  onConfirme() {
    Swal.fire({
      title: '',
      text: 'Confirmez-vous l\'enregistrement ?',
      icon: 'warning',
      showCancelButton: true,
      showCloseButton: true,
      confirmButtonText: 'Confirmer <i class="fas fa-check"></i>',
      cancelButtonText: 'Annuler <i class="feather icon-x-circle"></i>',
      confirmButtonColor: '#1bc943',
      reverseButtons: true
    }).then((willDelete) => {
      if (!willDelete.dismiss) {
        this.onSubmit();
      }
    });
  }

  toast(msg: string, title: string, type: string) {
    if (type == 'info') {
      this.toastr.info(msg, title);
    } else if (type == 'success') {
      this.toastr.success(msg, title);
    } else if (type == 'warning') {
      this.toastr.warning(msg, title);
    } else if (type == 'error') {
      this.toastr.error(msg, title);
    }
  }

  get f() { return this.form.controls; }
  get appartement() { return this.form.get('appartements') as FormArray; }
}